import type { Event } from '@sentry/nextjs'

const REDACTED = '[redacted]'
const MAX_SCRUB_DEPTH = 6
const SENSITIVE_KEY =
  /pass(word)?|secret|token|authorization|cookie|api[-_]?key|document_number|cpf|card|cvv/i

interface LogLike {
  level: string
  attributes?: Record<string, unknown>
}

// Remove valores de chaves sensíveis (senha, token, CPF, cartão...) sem mutar
// o objeto original.
export function scrubValue(value: unknown, depth = 0): unknown {
  if (depth > MAX_SCRUB_DEPTH || value === null || typeof value !== 'object') {
    return value
  }

  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item, depth + 1))
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      SENSITIVE_KEY.test(key) ? REDACTED : scrubValue(item, depth + 1),
    ])
  )
}

// Logs de debug/trace não valem o volume; o resto passa sem dados sensíveis.
export function beforeSendLog<T extends LogLike>(log: T): T | null {
  if (log.level === 'debug' || log.level === 'trace') {
    return null
  }

  if (!log.attributes) {
    return log
  }

  return {
    ...log,
    attributes: scrubValue(log.attributes) as T['attributes'],
  }
}

// Remove cookies e headers de autenticação dos eventos de erro.
export function scrubEvent<T extends Event>(event: T): T {
  if (!event.request) {
    return event
  }

  return {
    ...event,
    request: {
      ...event.request,
      cookies: undefined,
      headers: scrubValue(event.request.headers) as Record<string, string>,
      data: scrubValue(event.request.data),
    },
  }
}

export const sharedInitOptions = {
  // Logs estruturados (Sentry.logger.*) habilitados nos 3 runtimes
  enableLogs: true,
  beforeSendLog,
  // Nunca envia IP/cookies/headers por padrão; o usuário é identificado só
  // pelo id via Sentry.setUser
  sendDefaultPii: false,
  normalizeDepth: MAX_SCRUB_DEPTH,
  initialScope: {
    tags: {
      brand: process.env.NEXT_PUBLIC_UNITY_ID,
      service: 'zamply-courses',
    },
  },
}
